import { get } from '../client/axiosClient';
import { dashboardEndpoints } from '../endpoints/report.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  PendingTimesheetWidget, SubmittedComparisonWidget, HoursVsMinimumWidget,
  EngagementStatusWidget, WipSummaryWidget,
} from '../types/report.types';

function buildParams(managerId?: string, weekStartDate?: string): Record<string, any> {
  const p: Record<string, any> = {};
  if (managerId) p.managerId = managerId;
  if (weekStartDate) p.weekStartDate = weekStartDate;
  return p;
}

class DashboardService {
  async getPendingTimesheets(managerId?: string, weekStartDate?: string): Promise<PendingTimesheetWidget> {
    const params = buildParams(managerId, weekStartDate);
    const endpoint = Object.keys(params).length
      ? buildEndpointWithQuery(dashboardEndpoints.pendingTimesheetsPath(), params)
      : dashboardEndpoints.pendingTimesheets();
    const response = await get<any>(endpoint);
    return response.data?.data ?? { pendingCount: 0, weekStartDate: '', employees: [] };
  }

  async getSubmittedComparison(managerId?: string, weekStartDate?: string): Promise<SubmittedComparisonWidget> {
    const params = buildParams(managerId, weekStartDate);
    const endpoint = Object.keys(params).length
      ? buildEndpointWithQuery(dashboardEndpoints.submittedComparisonPath(), params)
      : dashboardEndpoints.submittedComparison();
    const response = await get<any>(endpoint);
    return response.data?.data ?? {
      thisWeekStart: '', lastWeekStart: '',
      thisWeekCount: 0, lastWeekCount: 0, totalReportees: 0, changePercent: 0,
      recentWeeks: [],
    };
  }

  async getHoursVsMinimum(managerId?: string, weekStartDate?: string): Promise<HoursVsMinimumWidget> {
    const params = buildParams(managerId, weekStartDate);
    const endpoint = Object.keys(params).length
      ? buildEndpointWithQuery(dashboardEndpoints.hoursVsMinimumPath(), params)
      : dashboardEndpoints.hoursVsMinimum();
    const response = await get<any>(endpoint);
    return response.data?.data ?? {
      weekStartDate: '', totalLoggedMinutes: 0, totalMinimumMinutes: 0, employees: [],
    };
  }

  async getEngagementStatus(managerId?: string): Promise<EngagementStatusWidget> {
    const endpoint = managerId
      ? buildEndpointWithQuery(dashboardEndpoints.engagementStatusPath(), { managerId })
      : dashboardEndpoints.engagementStatus();
    const response = await get<any>(endpoint);
    return response.data?.data ?? { total: 0, statusCounts: [] };
  }

  async getWipSummary(
    managerId?: string,
    groupBy: string = 'Client',
    fromDate?: string,
    toDate?: string,
  ): Promise<WipSummaryWidget> {
    const params: Record<string, any> = { groupBy };
    if (managerId) params.managerId = managerId;
    if (fromDate) params.fromDate = fromDate;
    if (toDate) params.toDate = toDate;
    const endpoint = buildEndpointWithQuery(dashboardEndpoints.wipSummaryPath(), params);
    const response = await get<any>(endpoint);
    return response.data?.data ?? {
      groupBy, items: [], totalWip: 0, totalBudget: 0, totalInvoiced: 0, totalOutstanding: 0,
    };
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
